import { injectable } from "inversify";
import IRepository from 'data/IRepository';
import { inversifyContainer } from 'infrastructure/ioc/ioc.container';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import { Contract_CapacityElectricity } from 'data/endpoints';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import IReservedCapacityFormService from "./IReservedCapacityFormService";
import CapacityElectricityDTO from "features/contracts/dtos/CapacityElectricityDTO";
import ContractDropdownDTO from "features/contracts/dtos/ContractDropdownDTO";
import OptionType from "features/shared/components/selectInput/OptionType";
import { ValueActionType } from "features/shared/forms/hooks/useForm";
import { formatNumber } from "features/shared/helpers/Formatters";
import { ContractDurationType } from "features/contracts/enums/contractDurationType";
import { formatISO } from "date-fns";
import { Nullable } from "features/shared/types/nullable";

@injectable()
export default class ReservedCapacityFormService implements IReservedCapacityFormService {
  private readonly _capacityRepositry: IRepository<CapacityElectricityDTO>;
    
  constructor() {
    this._capacityRepositry = inversifyContainer.get<IRepository<CapacityElectricityDTO>>(IocTypes.Repository);
  }

  async fetchElectricityCapacities(contractId: string, year: number, onlyValid: boolean = true): Promise<CapacityElectricityDTO[]> {
    let routeParams = getRouteParams("contractId", contractId);
    routeParams.set("year", String(year));

    let capacities = await this._capacityRepositry.get({
      url: Contract_CapacityElectricity,
      routeParams: routeParams
    });

    if (onlyValid) {
      return capacities.filter(x => x.type === "Valid");
    } else {
      return capacities;
    }
  }

  generateYears(contract: ContractDropdownDTO): OptionType[] {
    let result: OptionType[] = [];
    let boundaries = this.getContractYearBoundaries(contract.terminationDate!, contract.contractDurationType!);

    let startYear = boundaries[0];
    let endYear = boundaries[1];

    for (let i = startYear; i <= endYear; i++) {
      result.push({
        value: i.toString(),
        name: i.toString(),
      });      
    }

    return result;
  }

  getContractYearBoundaries(termination: Date, durationType: ContractDurationType): number[] {
    let actualDate = new Date();
    let terminationDate = new Date(termination);

    let startYear = actualDate.getMonth() == 11 ? actualDate.getFullYear() + 1 : actualDate.getFullYear();
    let endYear = startYear;

    if (terminationDate.getFullYear() > startYear) {
      endYear = startYear + 1;
    }
    if (durationType === "CertainTimeProlongation") {
      endYear = startYear + 1;
    }  

    return [startYear, endYear];
  }

  generateMonths(year: number): OptionType[] {
    let result: OptionType[] = [];
    let actulDate = new Date();
    let actualMonth = actulDate.getMonth() + 1;
    let startMonth = actualMonth + 1;

    if (year > actulDate.getFullYear() || actualMonth == 12) {
      startMonth = 1;
    }

    for (let i = startMonth; i <= 12; i++) {
      result.push({
        value: String(i),
        name: `${i}/${year}`,
      });
    }

    return result;
  }

  setReservedCapacities(capaticies: CapacityElectricityDTO[], setFormValue: (key: string, value: any, action: ValueActionType) => void, setOriginalCapacityDesc: boolean): void {
    if (capaticies.length > 0) {
      for (const capacity of capaticies) {
        let value = formatNumber(capacity.monthlyCapacity!, 3);
        setFormValue(this.getInputName(capacity, "DESC_TMZMSMLEEKAP"), value, "add");
        if (setOriginalCapacityDesc) {
          setFormValue(this.getInputName(capacity, "DESC_TMZMSMLEEKAP", true), value, "add");   
        }
      }
    } 
    // empty array = reset previous values
    else {
      for (let i = 1; i <= 12; i++) {
        let capacity: CapacityElectricityDTO = { month: i };
        setFormValue(this.getInputName(capacity, "DESC_TMZMSMLEEKAP"), "", "remove");
        if (setOriginalCapacityDesc) {
          setFormValue(this.getInputName(capacity, "DESC_TMZMSMLEEKAP", true), "", "remove");   
        }
      }
    }
  }

  setDefaultReservedCapacities(capaticies: CapacityElectricityDTO[], selectedMonth: number, setFormValue: (key: string, value: any, action: ValueActionType) => void): void {
    for (const capacity of capaticies) {
      if (capacity.month! < selectedMonth) {
        let value = formatNumber(capacity.monthlyCapacity!, 3);
        setFormValue(this.getInputName(capacity, "DESC_TMZMSMLEEKAP"), value, "add");
      }
    }
  }


  setDefaultAnnualCapacity(capaticies: CapacityElectricityDTO[], selectedMonth: number, setFormValue: (key: string, value: any, action: ValueActionType) => void): void {
    const clearValues = () => {
      setFormValue("DESC_TMZMSMLEEKAPROCNI", "", "remove");
      setFormValue("DESC_TMZMSMLEEKAPROCNIPUV", "", "remove");
      setFormValue("DESC_TMZMSMLEEPRIKON", "", "remove");
      setFormValue("DESC_TMZMSMLEEPRIKONPUVOD", "", "remove");
    }

    if (capaticies.length > 0) {
      let capacityByMonth = capaticies.find(x => x.month === selectedMonth);

      if (capacityByMonth) {
        let yearlyCapacity = formatNumber(capacityByMonth.yearlyCapacity!, 3);
        setFormValue("DESC_TMZMSMLEEKAPROCNI", yearlyCapacity, "add");
        setFormValue("DESC_TMZMSMLEEKAPROCNIPUV", yearlyCapacity, "add");

        let reserverPower = formatNumber(capacityByMonth.reserverPower!, 3);
        setFormValue("DESC_TMZMSMLEEPRIKON", reserverPower, "add"); 
        setFormValue("DESC_TMZMSMLEEPRIKONPUVOD", reserverPower, "add");   
      } else {
        clearValues();
      }
    } else {
      clearValues();
    }
  }

  prepareValuesToSend(values: Record<string, any>): Record<string, string> {
    let result: Record<string, string> = {};
    for (const [key, value] of Object.entries(values)) {
      // in numbers replace comma to dot
      if (key.startsWith("DESC_")) {
        result[key] = String(value).replace(",", ".");
      } else {
        result[key] = value;
      }
    }
    // from year and month create date
    let month = Number(values["Month"]) < 10 ? `0${values["Month"]}` : String(values["Month"]);
    result["DESC_TMZMSMLEEDATUMPOZZME"] = `${values["Year"]}-${month}-01`;

    return result;
  }

  getStartMonth(capaticies: CapacityElectricityDTO[], values: Record<string, any>): Nullable<number> {
    for (const capacity of capaticies) {
      let inputName = this.getInputName(capacity, "DESC_TMZMSMLEEKAP");
      let value = values[inputName];

      if (value && value !== formatNumber(capacity.monthlyCapacity!, 3)) {
        return capacity.month!;
      }
    }

    return null;
  }

  private getInputName (capacity: CapacityElectricityDTO, prefix: string, isOriginal: boolean = false): string {
    let month = capacity.month! < 10 ? `0${capacity.month}` : String(capacity.month);   
    return `${prefix}${month}${isOriginal ? "PUV" : ""}`;
  }

  async hasDraftCapacity(contractId: string, selectedYear: number, years: OptionType[], actualYearCapacities: CapacityElectricityDTO[]): Promise<boolean> {
    let hasDraftCapacity = false;

    hasDraftCapacity = actualYearCapacities.some(x => x.type === "Draft");

    if (!hasDraftCapacity) {
      let yearsToCheck = years.filter(x => Number(x.value) !== selectedYear);

      for (const year of yearsToCheck) {
        let capacities = await this.fetchElectricityCapacities(contractId, Number(year.value), false);
        if (capacities.length > 0 && capacities.some(x => x.type === "Draft")) {
          hasDraftCapacity = true;
          break;
        }
      }
    }
    
    return hasDraftCapacity;
  }
}